import React from 'react';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { FiLoader, FiCheck, FiX, FiCopy } from 'react-icons/fi';
import { OrganizationSelect } from "@/components/OrganizationSelect";
import { Button } from "@/components/ui/button";
import { toast } from 'sonner';

const FormFields = ({
  formData,
  handleFormChange,
  formErrors = {},
  slugValidating = false,
  slugChecked = false,
  slugAvailable = null,
  preselect = false,
  disabledFields = {}
}) => {
  // Add copy URL functionality
  const [copied, setCopied] = React.useState(false);
  
  // Track if password protection is enabled (derived from formData on mount, then controlled)
  const [passwordEnabled, setPasswordEnabled] = React.useState(!!formData.password);
  
  // Sync passwordEnabled when formData.password changes externally (e.g., loading existing form)
  React.useEffect(() => {
    setPasswordEnabled(!!formData.password);
  }, [formData.password]);
  
  // Helper functions for form URL
  const getFormBaseUrl = () => {
    return import.meta.env.VITE_FORMS_FRONTEND_BASE_URL || 'https://forms.status.bot';
  };
  
  const getFormDisplayDomain = () => {
    const baseUrl = getFormBaseUrl();
    return baseUrl.replace(/^https?:\/\//, '');
  };
  
  const getFormFullUrl = (slug) => {
    const baseUrl = getFormBaseUrl();
    return `${baseUrl}/${slug}`;
  };
  
  const copyFormUrl = () => {
    const url = getFormFullUrl(formData.slug);
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Form URL copied to clipboard');
    
    // Reset the copied state after 2 seconds
    setTimeout(() => {
      setCopied(false);
    }, 2000);
  };
  
  const formUrl = getFormFullUrl(formData.slug);
  const displayDomain = getFormDisplayDomain();
  
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Label htmlFor="name" className="text-sm font-medium">Form Name</Label>
        <Input 
          id="name"
          value={formData.name}
          onChange={(e) => handleFormChange('name', e.target.value)}
          placeholder="October Marketing UAT Feedback"
          className={formErrors.name ? 'border-red-500' : ''}
          disabled={disabledFields.name}
        />
        {formErrors.name && (
          <p className="text-sm text-red-500">{formErrors.name}</p>
        )}
        <p className="text-xs text-muted-foreground">Give your form a descriptive name</p>
      </div>
      
      <div className="space-y-3">
        <Label htmlFor="slug" className="text-sm font-medium">Form URL</Label>
        {!disabledFields.slug ? (
          // Editable URL field for form creation
          <div className="space-y-2">
            <div className="flex items-center">
              <span className="text-xs text-muted-foreground whitespace-nowrap bg-muted px-3 py-2 rounded-l-md border-y border-l">
                {displayDomain}/
              </span>
              <div className="relative flex-1">
                <Input 
                  id="slug"
                  value={formData.slug}
                  onChange={(e) => handleFormChange('slug', e.target.value)}
                  placeholder="october-marketing-uat"
                  className={`pr-8 w-full rounded-l-none ${formErrors.slug ? 'border-red-500' : slugChecked && slugAvailable ? 'border-green-500' : ''}`}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  {slugValidating && <FiLoader className="h-4 w-4 animate-spin text-muted-foreground" />}
                  {!slugValidating && slugChecked && slugAvailable && <FiCheck className="h-4 w-4 text-green-500" />}
                  {!slugValidating && slugChecked && slugAvailable === false && <FiX className="h-4 w-4 text-red-500" />}
                </div>
              </div>
            </div>
            
            {formErrors.slug && (
              <p className="text-sm text-red-500">{formErrors.slug}</p>
            )}
            {!formErrors.slug && slugChecked && slugAvailable === false && (
              <p className="text-sm text-red-500">This form URL is already taken</p>
            )}
            {!formErrors.slug && slugChecked && slugAvailable && (
              <p className="text-sm text-green-500">This form URL is available</p>
            )}
          </div>
        ) : (
          // Read-only URL with copy button for form editing
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="flex-grow">
                <div className="flex items-center px-3 py-2 border rounded-md bg-muted/30 text-sm">
                  <a 
                    href={formUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="hover:underline text-foreground/80 truncate"
                  >
                    {displayDomain}/{formData.slug}
                  </a>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={copyFormUrl}
                className="h-10 w-10 shrink-0"
              >
                {copied ? <FiCheck className="h-4 w-4 text-green-500" /> : <FiCopy className="h-4 w-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Form URL cannot be changed after creation</p>
          </div>
        )}
        
        {!disabledFields.slug && (
          <p className="text-xs text-muted-foreground">The URL where users will access your form</p>
        )}
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="requiresPassword"
            checked={passwordEnabled}
            onCheckedChange={(checked) => {
              setPasswordEnabled(checked);
              if (!checked) {
                handleFormChange('password', '');
              }
            }}
            disabled={disabledFields.password}
          />
          <Label htmlFor="requiresPassword" className="text-sm font-medium cursor-pointer">
            Require password to access form
          </Label>
        </div>
        
        {passwordEnabled && (
          <>
            <Input 
              id="password"
              type="text"
              value={formData.password || ''}
              onChange={(e) => handleFormChange('password', e.target.value)}
              placeholder="Simple password for form access"
              className={formErrors.password ? 'border-red-500' : ''}
              disabled={disabledFields.password}
            />
            {formErrors.password && (
              <p className="text-sm text-red-500">{formErrors.password}</p>
            )}
          </>
        )}
        
        <p className="text-xs text-muted-foreground">
          {passwordEnabled 
            ? 'Users will need this password to access the form' 
            : 'Anyone with the link can access this form'}
        </p>
      </div>
      
      <div className="space-y-3">
        <Label htmlFor="organization" className="text-sm font-medium">Organization</Label>
        <OrganizationSelect
          preselect={preselect}
          value={formData.organization}
          onChange={(value) => handleFormChange('organization', value)}
          className={formErrors.organization ? 'border-red-500' : ''}
          disabled={disabledFields.organization}
        />
        {formErrors.organization && (
          <p className="text-sm text-red-500">{formErrors.organization}</p>
        )}
        <p className="text-xs text-muted-foreground">Assign this form to an organization</p>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label htmlFor="online" className="text-sm font-medium">Form Status</Label>
            <p className="text-xs text-muted-foreground">
              {formData.online ? 'Form is online and accepting submissions' : 'Form is offline and not accepting submissions'}
            </p>
          </div>
          <Switch
            id="online"
            checked={formData.online}
            onCheckedChange={(checked) => handleFormChange('online', checked)}
            disabled={disabledFields.online}
          />
        </div>
      </div>
    </div>
  );
};

export default FormFields; 